import { PdProductItem } from 'interfaces/line-item';
import { CartBundle, UpdateCartItem } from 'contexts/type/cart-type';
import { toCartItem } from 'contexts/cart/utils/to-cart-item';
import { SubscriptionInterval } from 'constants/order';
import { DiscountOrigin } from 'interfaces/cart';
import { Cents } from 'types/common';
import isEmpty from 'lodash/isEmpty';

interface BuildBundleIntervalUpdates {
  items: PdProductItem[];
  interval: SubscriptionInterval | '';
}

export const buildBundleIntervalUpdates = ({
  items,
  interval,
}: BuildBundleIntervalUpdates): UpdateCartItem[] =>
  items.map((item) => ({
    target: toCartItem({ item, shouldQuantity: false }),
    updateData: {
      variantId: String(item.variantId),
      quantity: item.quantity,
      bundleRefs: item.bundleRefs,
      interval: interval === '' ? undefined : interval,
    },
  }));

export interface TotalDiscount {
  type: string;
  amount: Cents;
  unit: string;
  value: number;
}

export interface BundleTotals {
  totalWeight: number;
  totalDefaultPrice: number;
  totalPrice: number;
  totalDiscounts: TotalDiscount[];
  pricePerKilo: number;
}

interface GetBundleTotals {
  items: PdProductItem[];
  discountOrigins?: DiscountOrigin[];
}

const DISCOUNT_TYPE_PRIORITY: Record<string, number> = {
  savings_plan: 10,
  dynamic_bundle: 20,
  coupon: 30,
};

const sortDiscounts = (a: TotalDiscount, b: TotalDiscount): number => {
  if (a.type === b.type) {
    return a.type.localeCompare(b.type);
  }

  return DISCOUNT_TYPE_PRIORITY[a.type] - DISCOUNT_TYPE_PRIORITY[b.type];
};

const KILOGRAM = 1000;

/**
 * Calculates total bundle weight, price before bundle discount and after
 * Calculate total discount and sort discounts by DISCOUNT_TYPE_PRIORITY
 */
export const getBundleTotals = ({ items, discountOrigins }: GetBundleTotals): BundleTotals => {
  const discountsByType = {};
  const totals = items.reduce(
    (acc, item) => {
      acc.totalWeight += item.grams * item.quantity;
      acc.totalPrice += item.price * item.quantity;
      acc.totalDefaultPrice += (item.compareAtPrice || item.defaultPrice) * item.quantity;

      if (discountOrigins && item.discounts?.length) {
        for (const discount of item.discounts) {
          const origin = discountOrigins[discount.originIndex];
          const type = origin?.type;
          if (!type) continue;

          const discountAmount = discount.amount * item.quantity;

          const existing = discountsByType[type];
          if (existing) {
            existing.amount += discountAmount;
          } else {
            discountsByType[type] = {
              type,
              amount: discountAmount,
              unit: origin.unit,
              value: origin.value,
            };
          }
        }
      }

      return acc;
    },
    { totalWeight: 0, totalDefaultPrice: 0, totalPrice: 0 }
  );

  const discountByTypeEntries: [string, TotalDiscount][] = isEmpty(discountsByType)
    ? []
    : Object.entries(discountsByType);

  const totalDiscounts = discountByTypeEntries
    .map(([, value]: [string, TotalDiscount]) => value)
    .sort(sortDiscounts);

  return {
    ...totals,
    totalDiscounts,
    pricePerKilo: totals.totalWeight ? (totals.totalPrice / totals.totalWeight) * KILOGRAM : 0,
  };
};

export type BundleGroup = CartBundle & {
  bundleItems: PdProductItem[];
};

export const groupBundleItems = (items: PdProductItem[]): Array<PdProductItem | BundleGroup> => {
  const result: Array<PdProductItem | BundleGroup> = [];
  const bundlesById = new Map<string, BundleGroup>();

  for (const item of items) {
    const bundleRef = item.bundleRefs?.[0];

    if (!bundleRef) {
      result.push(item);
      continue;
    }

    let bundle = bundlesById.get(bundleRef.bundleId);
    if (!bundle) {
      bundle = { bundleId: bundleRef.bundleId, quantity: bundleRef.quantity, bundleItems: [] };
      bundlesById.set(bundleRef.bundleId, bundle);
      result.push(bundle);
    }

    bundle.bundleItems.push(item);
  }

  return result;
};
